import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, CheckCircle, Circle, Lock, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LearningPathDetailPage = () => {
  const navigate = useNavigate();
  const { pathId } = useParams();
  const { user } = useAuth();
  const [pathData, setPathData] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});

  useEffect(() => {
    fetchPathDetails();
  }, [pathId]);

  const fetchPathDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/learning/paths/${pathId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.path?.requires_premium) {
        toast.error('Esta trilha requer plano premium');
        navigate('/learning');
        return;
      }
      
      setPathData(data.path);
      setModules(data.modules || []);
      
      // Expand first incomplete module by default
      const firstIncomplete = data.modules?.findIndex(m => m.progress_percent < 100);
      if (firstIncomplete >= 0) {
        setExpandedModules({ [data.modules[firstIncomplete].id]: true });
      } else if (data.modules?.length > 0) {
        setExpandedModules({ [data.modules[0].id]: true });
      }
    } catch (error) {
      toast.error('Erro ao carregar trilha');
      navigate('/learning');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleLessonClick = (moduleId, lessonId) => {
    navigate(`/learning/${pathId}/${moduleId}/${lessonId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse text-forest text-lg">Carregando...</div>
      </div>
    );
  }

  if (!pathData) {
    return null;
  }

  const totalLessons = modules.reduce((acc, m) => acc + m.total_lessons, 0);
  const completedLessons = modules.reduce((acc, m) => acc + m.completed_lessons, 0);
  const overallProgress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div className="min-h-screen bg-sand" data-testid="learning-path-detail">
      {/* Header */}
      <div 
        className="text-white p-4 pb-6"
        style={{ backgroundColor: pathData.color }}
      >
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate('/learning')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-white/70 text-sm">Trilha {pathData.level === 'beginner' ? 'Iniciante' : pathData.level === 'intermediate' ? 'Intermediária' : 'Avançada'}</span>
        </div>
        
        <div className="flex items-center gap-4">
          <span className="text-4xl">{pathData.icon}</span>
          <div>
            <h1 className="text-2xl font-bold">{pathData.name}</h1>
            <p className="text-white/80 text-sm mt-1">{pathData.description}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-4 bg-white/20 rounded-xl p-3">
          <div className="flex items-center justify-between text-sm mb-2">
            <span>{completedLessons} de {totalLessons} lições completas</span>
            <span className="font-semibold">{overallProgress}%</span>
          </div>
          <div className="h-2 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Modules List */}
      <div className="p-4 space-y-3 max-w-lg mx-auto -mt-2">
        {modules.map((module, moduleIndex) => {
          const isExpanded = expandedModules[module.id];
          const isComplete = module.progress_percent === 100;
          
          return (
            <div 
              key={module.id}
              className="bg-white rounded-2xl shadow-sm border border-forest/10 overflow-hidden"
              data-testid={`module-${module.id}`}
            >
              {/* Module Header */}
              <button
                onClick={() => toggleModule(module.id)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-sand/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-sand flex items-center justify-center text-xl">
                  {module.icon}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-stone">Módulo {moduleIndex + 1}</span>
                    {isComplete && <CheckCircle className="w-4 h-4 text-green-500" />}
                  </div>
                  <h3 className="font-semibold text-forest truncate">{module.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-1.5 bg-sand rounded-full overflow-hidden max-w-[100px]">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${module.progress_percent}%`,
                          backgroundColor: pathData.color 
                        }}
                      />
                    </div>
                    <span className="text-xs text-stone">
                      {module.completed_lessons}/{module.total_lessons}
                    </span>
                  </div>
                </div>
                
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-stone shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-stone shrink-0" />
                )}
              </button>

              {/* Lessons List */}
              {isExpanded && (
                <div className="border-t border-forest/10">
                  {module.lessons.map((lesson, lessonIndex) => (
                    <button
                      key={lesson.id}
                      onClick={() => handleLessonClick(module.id, lesson.id)}
                      className="w-full p-3 pl-14 flex items-center gap-3 text-left hover:bg-sand/30 transition-colors border-b border-forest/5 last:border-b-0"
                      data-testid={`lesson-${lesson.id}`}
                    >
                      {lesson.completed ? (
                        <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                      ) : (
                        <Circle className="w-5 h-5 text-stone/40 shrink-0" />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${lesson.completed ? 'text-stone' : 'text-forest'} font-medium truncate`}>
                          {lessonIndex + 1}. {lesson.name}
                        </p>
                        <p className="text-xs text-stone/70 truncate">{lesson.description}</p>
                      </div>
                      
                      <BookOpen className="w-4 h-4 text-stone/40 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LearningPathDetailPage;
